import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { EyeIcon, EyeOffIcon, GripVerticalIcon, KanbanSquareIcon, ListChecksIcon, PhoneIcon, SlidersHorizontalIcon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { updateLeadStage } from '../../services/leadService';
import { reorderStages, updateStage } from '../../services/organizationService';
import { usePreferences } from '../../hooks/usePreferences';
import { formatDate, fullName } from '../../utils/format';
import { resolveLeadContact } from '../../utils/leadFields';
import { PageHeader } from '../../components/crm/PageHeader';
import { DemoBadge } from '../../components/crm/DemoBadge';
import { EmptyState } from '../../components/crm/EmptyState';
import { SourceBadge } from '../../components/crm/SourceBadge';
import { StageManager } from '../../components/crm/StageManager';
import { noteColor } from '../../components/crm/noteColors';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { FloatingPanel } from '../../components/shared/FloatingPanel';
import { Switch } from '../../components/ui/Switch';
import { Checkbox } from '../../components/ui/Checkbox';

/** Campos que se pueden mostrar en cada tarjeta del embudo. */
const CARD_FIELDS: {
  key: string;
  label: string;
}[] = [{
  key: 'company',
  label: 'Empresa'
}, {
  key: 'email',
  label: 'Email'
}, {
  key: 'budget',
  label: 'Presupuesto'
}, {
  key: 'service',
  label: 'Tipo de servicio'
}, {
  key: 'note',
  label: 'Última nota'
}, {
  key: 'owner',
  label: 'Comercial'
}, {
  key: 'date',
  label: 'Fecha'
}, {
  key: 'lastContact',
  label: 'Último contacto'
}];
export function Pipeline() {
  const {
    user,
    members,
    hasPermission
  } = useAuth();
  const {
    organizationId,
    leads,
    stages,
    sources,
    notes,
    isDemo
  } = useWorkspace();
  const [preferences, updatePreferences] = usePreferences();
  const cards = preferences.pipelineCards;
  const [dragging, setDragging] = useState<string | null>(null);
  const [overStage, setOverStage] = useState<string | null>(null);
  /** Qué se está arrastrando: un lead o una columna completa. */
  const [dragKind, setDragKind] = useState<'lead' | 'column' | null>(null);
  const [dragColumn, setDragColumn] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const hidden = preferences.pipelineHidden;
  const [cardsOpen, setCardsOpen] = useState(false);
  const [stagesOpen, setStagesOpen] = useState(false);
  const canEdit = hasPermission('leads.edit');
  const authorName = user ? fullName(user.firstName, user.lastName) : 'Sistema';
  const compact = cards.mode === 'compacto';
  function setCards(patch: Partial<typeof cards>) {
    updatePreferences({
      pipelineCards: {
        ...cards,
        ...patch
      }
    });
  }
  // El nombre y el teléfono se muestran siempre; `shows` decide el resto.
  function shows(field: string) {
    if (compact) return field === 'company';
    return cards.fields.includes(field);
  }
  function lastNote(leadId: string) {
    return notes.filter(n => n.leadId === leadId).sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
  }
  function setHidden(next: string[]) {
    updatePreferences({
      pipelineHidden: next
    });
  }

  /**
   * Soltar sobre una columna. Se distingue explícitamente qué se arrastra: un
   * lead cambia de estado, una columna cambia el orden del embudo. Nunca se
   * mezclan las dos acciones.
   */
  async function drop(stageId: string) {
    if (dragKind === 'column' && dragColumn && dragColumn !== stageId) {
      const ordered = stages.map(s => s.id);
      const from = ordered.indexOf(dragColumn);
      const to = ordered.indexOf(stageId);
      if (from >= 0 && to >= 0) {
        ordered.splice(to, 0, ordered.splice(from, 1)[0]);
        await reorderStages(organizationId, ordered);
      }
    } else if (dragKind === 'lead' && dragging) {
      await updateLeadStage(dragging, stageId, authorName);
    }
    setDragging(null);
    setDragColumn(null);
    setDragKind(null);
    setOverStage(null);
  }

  /** Guarda el nuevo nombre de la columna. El `stage_id` no cambia nunca. */
  async function commitRename(stageId: string) {
    const name = renameValue.trim();
    setRenaming(null);
    const current = stages.find(s => s.id === stageId);
    if (!name || !current || name === current.name) return;
    await updateStage(stageId, {
      name
    });
  }
  const columns = stages.filter(s => s.active && !hidden.includes(s.id));
  if (leads.length === 0) {
    return <>
        <PageHeader title="Embudo de ventas" description="Tus oportunidades por estado." data-id="element-1771" data-mp="1g35oer8ke0yb" />
        <EmptyState icon={<KanbanSquareIcon className="h-5 w-5" data-id="element-1773" data-mp="2vjnzod36agsj" />} title="Tu embudo está vacío" description="Cuando recibas leads desde tus landings aparecerán aquí para que puedas moverlos entre estados." action={<Link to="/app/landings" data-id="element-1774" data-mp="2z1ttlkxjmlgw">
              <Button data-id="element-1775" data-mp="342hv50jeekpd">Crear una landing</Button>
            </Link>} data-id="element-1772" data-mp="2vk643wzzo1bj" />
      </>;
  }
  return <>
      <PageHeader title="Embudo de ventas" description="Arrastra un lead para cambiar su estado. Usa el asa de la cabecera para mover la columna entera y pulsa su nombre para renombrarla." actions={<>
            {isDemo && <DemoBadge className="self-center" data-id="element-1777" data-mp="3hu6630hwv34r" />}
            <Button variant="secondary" leftIcon={<SlidersHorizontalIcon className="h-4 w-4" data-id="element-1779" data-mp="3hw8whlmz6ya0" />} onClick={() => setCardsOpen(true)} data-id="element-1778" data-mp="3huek4vprsehj">
              Personalizar tarjetas
            </Button>
            <Button variant="secondary" leftIcon={<ListChecksIcon className="h-4 w-4" data-id="element-1781" data-mp="3828tbpb9833x" />} onClick={() => setStagesOpen(true)} data-id="element-1780" data-mp="383bi9rp0nvh7">
              Gestionar estados
            </Button>
          </>} data-id="element-1776" data-mp="340547xvnx006" />

      {/* Columnas ocultas */}
      {stages.filter(s => s.active).length > columns.length && <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-surface px-4 py-3" data-id="element-1782" data-mp="9ecnmg40rhl2">
          <span className="text-xs font-medium text-muted-foreground" data-id="element-1783" data-mp="j8s2nhfxdmwk">
            Columnas ocultas:
          </span>
          {stages.filter(s => s.active && hidden.includes(s.id)).map(stage => <button key={stage.id} type="button" onClick={() => setHidden(hidden.filter(id => id !== stage.id))} className="inline-flex items-center gap-1.5 rounded-full bg-background px-2.5 py-1 text-xs font-medium text-foreground" data-id="element-1784" data-mp="a8bzk933ghs8">
                <EyeIcon className="h-3 w-3" aria-hidden="true" data-id="element-1785" data-mp="cihybaipy3l" />
                {stage.name}
              </button>)}
        </div>}

      <div className="-mx-4 overflow-x-auto px-4 pb-4 sm:mx-0 sm:px-0" data-id="element-1786" data-mp="3mqs40dqz6u17">
        <div className="flex min-w-max gap-4" data-id="element-1787" data-mp="3mopdztptp0wh">
          {columns.map(stage => {
          const stageLeads = leads.filter(l => l.stageId === stage.id);
          return <section key={stage.id} onDragOver={e => {
            if (!canEdit) return;
            e.preventDefault();
            setOverStage(stage.id);
          }} onDragLeave={() => setOverStage(null)} onDrop={() => drop(stage.id)} className={`w-[280px] flex-none rounded-2xl border bg-surface p-3 transition-colors ${overStage === stage.id ? 'border-primary bg-primary/5' : 'border-border'} ${dragColumn === stage.id ? 'opacity-60' : ''}`} data-id="element-1788" data-mp="3dp2bt5p9rr2d">
                <header className="mb-3 flex items-center justify-between gap-2 px-1" data-id="element-1789" data-mp="1y5bhbl9r5qwq">
                  <span className="flex min-w-0 items-center gap-1.5" data-id="element-1790" data-mp="226n65oksy83v">
                    {/* Asa exclusiva para mover la columna completa. */}
                    {canEdit && <span draggable role="button" tabIndex={0} aria-label={`Mover la columna ${stage.name}`} onDragStart={() => {
                  setDragKind('column');
                  setDragColumn(stage.id);
                }} onDragEnd={() => {
                  setDragKind(null);
                  setDragColumn(null);
                }} className="-ml-1 flex-none cursor-grab p-1 text-muted-foreground active:cursor-grabbing" data-id="element-1791" data-mp="1aimrmdei4u5k">
                        <GripVerticalIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1792" data-mp="qrhqzl3hih3d" />
                      </span>}
                    <span className="h-2.5 w-2.5 flex-none rounded-full" style={{
                  backgroundColor: stage.color
                }} aria-hidden="true" data-id="element-1793" data-mp="3bckvg5xalw8z" />
                    {renaming === stage.id ? <input autoFocus aria-label="Nombre del estado" value={renameValue} onChange={e => setRenameValue(e.target.value)} onBlur={() => commitRename(stage.id)} onKeyDown={e => {
                  if (e.key === 'Enter') commitRename(stage.id);
                  if (e.key === 'Escape') setRenaming(null);
                }} className="min-w-0 flex-1 rounded-md border border-primary bg-background px-2 py-1 text-sm font-semibold text-foreground focus:outline-none" data-id="element-1794" data-mp="3q7hd19qzrvyq" /> : <button type="button" disabled={!canEdit} onClick={() => {
                  if (!canEdit) return;
                  setRenameValue(stage.name);
                  setRenaming(stage.id);
                }} title={canEdit ? 'Renombrar columna' : undefined} className="truncate text-left text-sm font-semibold text-foreground hover:text-primary disabled:hover:text-foreground" data-id="element-1795" data-mp="7x5r9lbligvr">
                        {stage.name}
                      </button>}
                  </span>
                  <span className="flex flex-none items-center gap-1" data-id="element-1796" data-mp="rgl3c0ig17it">
                    <span className="rounded-full bg-background px-2 py-0.5 text-xs font-medium text-muted-foreground" data-id="element-1797" data-mp="wg7qb1o2q24t">
                      {stageLeads.length}
                    </span>
                    <button type="button" aria-label={`Ocultar columna ${stage.name}`} onClick={() => setHidden([...hidden, stage.id])} className="rounded-md p-1 text-muted-foreground hover:bg-background" data-id="element-1798" data-mp="mi9qri0rkiww">
                      <EyeOffIcon className="h-3.5 w-3.5" data-id="element-1799" data-mp="csgox8qfbi5u" />
                    </button>
                  </span>
                </header>

                <ul className="space-y-2" data-id="element-1800" data-mp="3uzo1tmhetdbt">
                  {stageLeads.map(lead => {
                const member = members.find(m => m.userId === lead.assignedTo);
                const note = lastNote(lead.id);
                const contact = resolveLeadContact(lead);
                const name = contact.name;
                return <li key={lead.id} data-id="element-1801" data-mp="2dv95vqeo1g6h">
                        <article draggable={canEdit} onDragStart={() => {
                    setDragKind('lead');
                    setDragging(lead.id);
                  }} onDragEnd={() => {
                    setDragKind(null);
                    setDragging(null);
                  }} className={`rounded-xl border border-border bg-background p-3 shadow-sm transition-opacity ${canEdit ? 'cursor-grab active:cursor-grabbing' : ''} ${dragging === lead.id ? 'opacity-50' : ''}`} data-id="element-1802" data-mp="1zuoo8xkhlqek">
                          {/* Nombre y teléfono son prioridad absoluta: se
                              muestran siempre, sin depender de la
                              personalización de la tarjeta. Los datos vacíos
                              simplemente no se pintan. */}
                          <Link to={`/app/leads/${lead.id}`} className="block truncate text-sm font-semibold text-foreground hover:text-primary" data-id="element-1803" data-mp="3gahufmq19y9a">
                            {name || 'Contacto sin nombre'}
                          </Link>
                          {contact.phone && <p className="mt-1 flex items-center gap-1.5 text-xs font-medium text-foreground" data-id="element-1804" data-mp="1dlzuc631mii9">
                              <PhoneIcon className="h-3 w-3 flex-none text-muted-foreground" aria-hidden="true" data-id="element-1805" data-mp="19lfv8wywt4th" />
                              <a href={`tel:${contact.phone.replace(/\s/g, '')}`} className="truncate hover:text-primary" onClick={e => e.stopPropagation()} data-id="element-1806" data-mp="lv3nbm3nz82w">
                                {contact.phone}
                              </a>
                            </p>}
                          {shows('company') && contact.company && <p className="mt-1 truncate text-xs text-muted-foreground" data-id="element-1807" data-mp="puuevs10ls8h">
                              {contact.company}
                            </p>}
                          {shows('email') && contact.email && <p className="truncate text-xs text-muted-foreground" data-id="element-1808" data-mp="21dey83agx7vm">
                              {contact.email}
                            </p>}
                          {shows('service') && lead.service && <p className="mt-1 truncate text-xs text-foreground" data-id="element-1809" data-mp="13z3rf2qbnbs7">
                              {lead.service}
                            </p>}
                          {shows('budget') && lead.budget && <p className="text-xs font-medium text-foreground" data-id="element-1810" data-mp="1cww6c5s4shf2">
                              {lead.budget}
                            </p>}
                          {shows('note') && note && <p className={`mt-2 line-clamp-2 rounded-md border-l-2 px-2 py-1 text-[11px] leading-snug text-foreground ${noteColor(note.color).bg} ${noteColor(note.color).border}`} data-id="element-1811" data-mp="k9tcvs2bawvo">
                              {note.body}
                            </p>}
                          {(cards.showSource || shows('date') || shows('owner') || shows('lastContact')) && <div className="mt-3 flex items-center justify-between gap-2" data-id="element-1812" data-mp="1iikf6gxuc1o">
                              {cards.showSource ? <SourceBadge source={lead.origin.source} sources={sources} data-id="element-1813" data-mp="3q4x7iiidpti5" /> : <span data-id="element-1814" data-mp="3q2axmc80n1wr" />}
                              <span className="flex items-center gap-2" data-id="element-1815" data-mp="3l4sqmrdk6wre">
                                {shows('date') && <span className="text-[11px] text-muted-foreground" data-id="element-1816" data-mp="3g5xyuzxm5xv5">
                                    {formatDate(lead.createdAt)}
                                  </span>}
                                {shows('lastContact') && lead.lastContactAt && <span className="text-[11px] text-muted-foreground" data-id="element-1817" data-mp="376b24rftxg8f">
                                    {formatDate(lead.lastContactAt)}
                                  </span>}
                                {shows('owner') && member && <Avatar name={fullName(member.firstName, member.lastName)} size="sm" data-id="element-1818" data-mp="2xdnaeb3pi2gk" />}
                              </span>
                            </div>}
                        </article>
                      </li>;
              })}
                  {stageLeads.length === 0 && <li className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground" data-id="element-1819" data-mp="2jggt6kymsbtg">
                      Sin leads en este estado
                    </li>}
                </ul>
              </section>;
        })}
        </div>
      </div>

      {/* Panel flotante para ver el embudo mientras se personalizan las tarjetas. */}
      <FloatingPanel open={cardsOpen} title="Personalizar tarjetas" description="Los cambios se aplican en el embudo al instante." onClose={() => setCardsOpen(false)} footer={<Button size="sm" onClick={() => setCardsOpen(false)} data-id="element-1821" data-mp="8nwehzb7888h">Listo</Button>} data-id="element-1820" data-mp="ilaiv0jul6kf">
        <div className="space-y-5" data-id="element-1822" data-mp="8p7f5q9ckzvd">
          <div data-id="element-1823" data-mp="3qtsm28t3cnl8">
            <p className="mb-2 text-sm font-medium text-foreground" data-id="element-1824" data-mp="3quas1x5k2g6c">Modo</p>
            <div className="grid grid-cols-2 gap-2" data-id="element-1825" data-mp="3vsc8eablk1e4">
              {(['compacto', 'ampliado'] as const).map(mode => <button key={mode} type="button" onClick={() => setCards({
              mode
            })} className={`min-h-[44px] rounded-xl border px-4 text-sm font-medium capitalize transition-colors ${cards.mode === mode ? 'border-primary bg-primary/5 text-foreground' : 'border-border text-muted-foreground hover:border-primary/40'}`} data-id="element-1826" data-mp="1p2fkntqc0xtx">
                  {mode}
                </button>)}
            </div>
            <p className="mt-2 text-xs text-muted-foreground" data-id="element-1827" data-mp="2cy0xtijpiv4w">
              El nombre y el teléfono aparecen siempre. En modo compacto se
              añade solo la empresa.
            </p>
          </div>

          <Switch id="pipeline-source" label="Mostrar fuente en las tarjetas" checked={cards.showSource} onChange={showSource => setCards({
          showSource
        })} data-id="element-1828" data-mp="2muvvjz85ori4" />

          <div data-id="element-1829" data-mp="2vw3ixm94w7xf">
            <p className="mb-2 text-sm font-medium text-foreground" data-id="element-1830" data-mp="2vwn2t8ich6e3">
              Campos en modo ampliado
            </p>
            <ul className="grid gap-2 sm:grid-cols-2" data-id="element-1831" data-mp="2rw35o4xru2ry">
              {CARD_FIELDS.map(field => <li key={field.key} data-id="element-1832" data-mp="11jdpt6qlb2r3">
                  <Checkbox id={`card-${field.key}`} label={field.label} disabled={compact} checked={cards.fields.includes(field.key)} onChange={e => setCards({
                fields: e.target.checked ? [...cards.fields, field.key] : cards.fields.filter(f => f !== field.key)
              })} data-id="element-1833" data-mp="11im2skb26dcf" />
                </li>)}
            </ul>
          </div>
        </div>
      </FloatingPanel>

      <StageManager open={stagesOpen} onClose={() => setStagesOpen(false)} organizationId={organizationId} stages={stages} data-id="element-1834" data-mp="36i3p3rwgnpop" />
    </>;
}